// Where every call to a back-end function lands, inside the library and out.
//
// Under node the function is imported and called in process, exactly as
// before. In the browser the call becomes a POST straight to the application
// server, carrying a token the server minted, so the client never holds an
// Airtable or AWS credential.
//
// It used to go through a netlify function that held the server secret and
// forwarded. That hop is gone because netlify caps a function at 30 seconds,
// and several of these functions legitimately run for minutes.
//
// The exports here mirror the back-end functions one for one, by name and by
// signature. A caller changes its import path and nothing else.
//
// Nothing in this file changes what a function does. The one piece of
// behaviour it has to supply is what to return when the call never made it to
// the server, and that value is copied from each function's own catch block,
// so a transport failure is indistinguishable from the failure it would have
// produced in process.

import * as libCon from '../Constants.js';

// Explicit specifiers rather than a computed path. `import('./' + name)` makes
// webpack build a context out of the whole folder and bundle all of it; these
// become lazy chunks the browser never fetches, and resolve normally in node.
const MODULES = {
  airtableFunctions: () => import('./airtableFunctions.js'),
  AWSFunctions: () => import('./AWSFunctions.js'),
  documentsFunctions: () => import('./documentsFunctions.js'),
  fitbitFunctions: () => import('./fitbitFunctions.js'),
  googleHealthFunctions: () => import('./googleHealthFunctions.js'),
};

// The server refuses anything without a token, and obtaining one is the app's
// business: it goes through a netlify function that checks the app password
// first. Rather than reach into the app from the library, the app hands the
// router a way to get one at start up.
//
// Async, so the provider can mint or renew on demand. That removes the race
// between logging in and the first call, which would otherwise have to be
// ordered by hand at every entry point.
let apiTokenProvider = async () => null;

/**
 * Registers how the router should obtain the current API token. Call once,
 * from the application's entry point.
 * @param {() => Promise<string | null>} provider
 */
export const setApiTokenProvider = (provider) => {
  apiTokenProvider = provider;
};

/**
 * Runs the function in process. Node only.
 */
const callLocally = async (moduleName, functionName, args) => {
  const namespace = await MODULES[moduleName]();

  return namespace[functionName](...args);
};

/**
 * Runs the function on the application server.
 *
 * Returns onTransportFailure for anything that stops the call reaching the
 * function, which covers a dead network, a rejected token and an error inside
 * the function itself. The server deliberately reports the last of these as an
 * opaque 500, since its exceptions carry the credentials they were made with,
 * so this cannot tell it apart from the others and does not try to.
 */
const callRemotely = async (functionName, args, onTransportFailure) => {
  let token;

  try {
    token = await apiTokenProvider();
  } catch (error) {
    console.log(`[router] ${functionName} could not obtain an API token:`, error.message);
    return onTransportFailure;
  }

  let response;

  try {
    response = await fetch(`${libCon.API_URL}/api/${functionName}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': token ?? '',
      },
      body: JSON.stringify({ args }),
    });
  } catch (error) {
    console.log(`[router] ${functionName} could not reach the server:`, error.message);
    return onTransportFailure;
  }

  let payload;

  try {
    payload = await response.json();
  } catch {
    console.log(`[router] ${functionName} got a non JSON response (status ${response.status})`);
    return onTransportFailure;
  }

  if (!response.ok || payload?.ok !== true) {
    console.log(`[router] ${functionName} failed (status ${response.status}):`, payload?.error, payload?.requestId ?? '');
    return onTransportFailure;
  }

  return payload.result;
};

/**
 * Builds the stand in for one back-end function.
 *
 * @param {string} moduleName          the back-end file it lives in
 * @param {string} functionName        its exported name
 * @param {*} onTransportFailure       what to return when the call cannot be made,
 *                                     taken from that function's own catch block
 */
const route =
  (moduleName, functionName, onTransportFailure = [libCon.NETWORK_ERROR, null]) =>
  async (...args) => {
    if (libCon.detectEnvironment() === libCon.NODE) return callLocally(moduleName, functionName, args);

    // JSON.stringify turns a trailing undefined into null, which would arrive
    // as an explicit null and beat the parameter's default. Dropping them lets
    // the library apply its own defaults, the way an in process call does.
    while (args.length > 0 && args[args.length - 1] === undefined) args.pop();

    return callRemotely(functionName, args, onTransportFailure);
  };

// Airtable
export const getSensorBySerial = route('airtableFunctions', 'getSensorBySerial');
export const getReceivedSensorFilesBySerial = route('airtableFunctions', 'getReceivedSensorFilesBySerial');
export const getPhoneBySerial = route('airtableFunctions', 'getPhoneBySerial');
export const getPhonePlacementByPhoneSerial = route('airtableFunctions', 'getPhonePlacementByPhoneSerial');
export const getParticipant = route('airtableFunctions', 'getParticipant');
export const getHouse = route('airtableFunctions', 'getHouse');
export const getRecordByInternalId = route('airtableFunctions', 'getRecordByInternalId');
export const getIssuesByIds = route('airtableFunctions', 'getIssuesByIds');
export const getPendingQuestionsParticipantIntakeForm = route(
  'airtableFunctions',
  'getPendingQuestionsParticipantIntakeForm'
);
export const getPendingQuestionsHouseIntakeForm = route('airtableFunctions', 'getPendingQuestionsHouseIntakeForm');
export const getPlacements = route('airtableFunctions', 'getPlacements');
export const getLatestGoveeActivationCodes = route('airtableFunctions', 'getLatestGoveeActivationCodes');
export const endPlacement = route('airtableFunctions', 'endPlacement');
export const removeSensorFromBundle = route('airtableFunctions', 'removeSensorFromBundle');
export const logBatteryChange = route('airtableFunctions', 'logBatteryChange');
export const markIssueAsReadyForRevision = route('airtableFunctions', 'markIssueAsReadyForRevision');
export const uploadCollectionRecord = route('airtableFunctions', 'uploadCollectionRecord');
export const updateAccountLastSynced = route('airtableFunctions', 'updateAccountLastSynced');
export const uploadMapLabReportRecord = route('airtableFunctions', 'uploadMapLabReportRecord');

// Whole table reads. The dashboards load these once at start up and derive
// everything else from them in the browser.
export const getAllParticipants = route('airtableFunctions', 'getAllParticipants');
export const getAllSensors = route('airtableFunctions', 'getAllSensors');
export const getAllHouses = route('airtableFunctions', 'getAllHouses');
export const getAllEmails = route('airtableFunctions', 'getAllEmails');
export const getAllPhones = route('airtableFunctions', 'getAllPhones');
export const getAllPhonesPlacement = route('airtableFunctions', 'getAllPhonesPlacement');
export const getAllWearables = route('airtableFunctions', 'getAllWearables');
export const getAllWearablePlacements = route('airtableFunctions', 'getAllWearablePlacements');
export const getAllSensorHousePlacement = route('airtableFunctions', 'getAllSensorHousePlacement');
export const getAllParticipantPlacement = route('airtableFunctions', 'getAllParticipantPlacement');
export const getAllIssues = route('airtableFunctions', 'getAllIssues');
export const getAllCollectionRecords = route('airtableFunctions', 'getAllCollectionRecords');
export const getAllBatteryChangeRecords = route('airtableFunctions', 'getAllBatteryChangeRecords');
export const getAllGoveeActivationCodes = route('airtableFunctions', 'getAllGoveeActivationCodes');
export const getAllReceivedSensorFiles = route('airtableFunctions', 'getAllReceivedSensorFiles');

export const getEmail = route('airtableFunctions', 'getEmail');
export const getWearablePlacement = route('airtableFunctions', 'getWearablePlacement');
export const markIssueAsDone = route('airtableFunctions', 'markIssueAsDone');
export const addNoteToIssue = route('airtableFunctions', 'addNoteToIssue');
export const updateFieldforRecord = route('airtableFunctions', 'updateFieldforRecord');

// The data half of the participants export. utils/exportFunctions.js turns what
// this returns into the spreadsheet.
export const getParticipantsForExports = route('airtableFunctions', 'getParticipantsForExports');

// syncRecordFrontEnd is the only one that returns four values. Its own network
// branch returns this exact tuple, and callers destructure all four.
export const syncRecordFrontEnd = route('airtableFunctions', 'syncRecordFrontEnd', [
  libCon.NETWORK_ERROR,
  'networkError',
  null,
  null,
]);

// AWS
export const getParticipantReportFromBucket = route('AWSFunctions', 'getParticipantReportFromBucket');
export const getApplictationVersion = route('AWSFunctions', 'getApplictationVersion');
export const getCoverageSummaryFromBucket = route('AWSFunctions', 'getCoverageSummaryFromBucket');

// Mints the presigned PUT that utils/uploadFunctions.js sends the bytes to.
// The URL is the only part of an upload that needs a credential, and it is
// small enough to travel as ordinary JSON.
export const createUploadURL = route('AWSFunctions', 'createUploadURL');

// Returns a bare status rather than a tuple.
export const uploadToBucket = route('AWSFunctions', 'uploadToBucket', libCon.NETWORK_ERROR);

// Documents
//
// Both mint a presigned GET for the share bucket. The URL is the whole answer:
// the bytes are .xlsx and .docx and never come through /api, so the browser
// takes the URL straight to S3.
export const getLabAndSurveySummaryURL = route('documentsFunctions', 'getLabAndSurveySummaryURL');
export const getCurrentSurveysURL = route('documentsFunctions', 'getCurrentSurveysURL');

// Fitbit
//
// Every route here takes an email address and nothing else. The functions that
// take a raw token still exist, but they are tagged internal and the server
// will not dispatch to them, so there is no client-side spelling of them to
// route: a token must never be in one of these request bodies.

// Mints the consent URL. The redirect URI has to match the callback this
// server serves and the state is signed with the server's secret, so this
// cannot be built in the browser.
export const getFitbitAuthURLForEmail = route('fitbitFunctions', 'getFitbitAuthURLForEmail');

// Looks the refresh token up itself and writes the new pair back to Airtable.
export const refreshAndUpdateTokenForEmail = route('fitbitFunctions', 'refreshAndUpdateTokenForEmail');

// The whole wearable refresh: probe, refresh if stale, read the last sync and
// the last heart rate, store both. Returns [status, { recordId: fields }].
// One call rather than four, because the caller is a phone on a field network.
export const refreshWearableSyncForEmail = route('fitbitFunctions', 'refreshWearableSyncForEmail');

// Bare status, as above.
export const testFitbitApiForEmail = route('fitbitFunctions', 'testFitbitApiForEmail', libCon.NETWORK_ERROR);

// Bare status, and its catch answers ATF_UNAUTHORIZED rather than a network
// error, so an unreachable server reads the same as an unusable token.
export const getFitbitTokenStatusForEmail = route(
  'fitbitFunctions',
  'getFitbitTokenStatusForEmail',
  libCon.ATF_UNAUTHORIZED
);

// Google Health
// Mints the consent URL. The client id and the state signing secret are the
// server's, so this cannot be built in the browser.
export const getGoogleHealthAuthURLForEmail = route('googleHealthFunctions', 'getGoogleHealthAuthURLForEmail');

// Bare status, like testFitbitApiForEmail. Refreshes and retries on its own,
// which is what keeps it from failing an hour after an account is linked.
export const testGoogleHealthApiForEmail = route(
  'googleHealthFunctions',
  'testGoogleHealthApiForEmail',
  libCon.NETWORK_ERROR
);

export const refreshGoogleHealthTokenForEmail = route('googleHealthFunctions', 'refreshGoogleHealthTokenForEmail');

// Bare ATF_* status, non refreshing, the twin of getFitbitTokenStatusForEmail.
// Its transport failure is ATF_UNAUTHORIZED for the same reason that one's is:
// a server that cannot be reached should read the same as a token that cannot
// be used, rather than as a separate kind of answer the viewers have to handle.
export const getGoogleHealthTokenStatusForEmail = route(
  'googleHealthFunctions',
  'getGoogleHealthTokenStatusForEmail',
  libCon.ATF_UNAUTHORIZED
);

// The Google twin of refreshWearableSyncForEmail. Returns [status, { recordId: fields }].
export const refreshGoogleHealthWearableSyncForEmail = route(
  'googleHealthFunctions',
  'refreshGoogleHealthWearableSyncForEmail'
);
